import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  ChevronLeftIcon,
  ChevronRightIcon,
  MapPinIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  HeartIcon,
  ClockIcon,
  CurrencyYenIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import JobCard from '../components/JobCard';
import { useAuth } from '../context/AuthContext';
import { ALL_PREFECTURES, getPrefecturesByRegion, REGIONS } from '../constants/prefectures';
import { API_BASE } from '../lib/config';

const API = `${API_BASE}`;

const AreaSearchPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  // State management
  const [currentStep, setCurrentStep] = useState('prefectures'); // prefectures, cities, jobs
  const [cities, setCities] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedPrefecture, setSelectedPrefecture] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showRegions, setShowRegions] = useState(true);
  const [favorites, setFavorites] = useState(new Set());
  
  // Filter states
  const [filters, setFilters] = useState({
    sortBy: 'created_at', // created_at, hourly_rate_desc, hourly_rate_asc, start_date
    minRate: '',
    maxRate: '',
    experienceRequired: null, // null, true, false
    sameDayOk: null,
    category: '',
    workDate: '' // today, tomorrow, specific_date
  });

  useEffect(() => {
    // Load saved area preferences and favorites
    const savedPrefecture = localStorage.getItem('selectedPrefecture');
    const savedCity = localStorage.getItem('selectedCity');
    const savedFavorites = JSON.parse(localStorage.getItem('favoriteJobs') || '[]');
    setFavorites(new Set(savedFavorites));
    
    // If user has location in profile, use that as default
    if (user && user.prefecture) {
      if (savedPrefecture === user.prefecture && savedCity === user.city) {
        setSelectedPrefecture(user.prefecture);
        setSelectedCity(user.city);
        setCurrentStep('jobs');
        fetchJobsByArea(user.prefecture, user.city);
      } else {
        // Show user's area as suggestion
        setSelectedPrefecture(user.prefecture);
        setCurrentStep('cities');
        fetchCities(user.prefecture);
      }
    } else if (savedPrefecture && savedCity) {
      setSelectedPrefecture(savedPrefecture);
      setSelectedCity(savedCity);
      setCurrentStep('jobs');
      fetchJobsByArea(savedPrefecture, savedCity);
    }
  }, [user]);

  const fetchCities = async (prefecture) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/locations/cities?prefecture=${prefecture}`);
      setCities(response.data.cities || []);
      setCurrentStep('cities');
    } catch (error) {
      console.error('Failed to fetch cities:', error);
      setCities([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobsByArea = async (prefecture, city) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        prefecture,
        city,
        limit: 50,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '' && value !== null)
        )
      });

      const response = await axios.get(`${API}/jobs/search?${params}`);
      setJobs(response.data || []);
      setCurrentStep('jobs');
      
      // Save area preferences
      localStorage.setItem('selectedPrefecture', prefecture);
      localStorage.setItem('selectedCity', city);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePrefectureSelect = (prefecture) => {
    setSelectedPrefecture(prefecture);
    fetchCities(prefecture);
  };

  const handleCitySelect = (city) => {
    setSelectedCity(city);
    fetchJobsByArea(selectedPrefecture, city);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    if (selectedPrefecture && selectedCity) {
      fetchJobsByArea(selectedPrefecture, selectedCity);
    }
    setShowFilters(false);
  };

  const resetFilters = () => {
    setFilters({
      sortBy: 'created_at',
      minRate: '',
      maxRate: '',
      experienceRequired: null,
      sameDayOk: null,
      category: '',
      workDate: ''
    });
  };

  const toggleFavorite = async (jobId) => {
    if (!user) {
      // Redirect to login if not authenticated
      navigate('/login');
      return;
    }

    try {
      await axios.post(`${API}/favorites/${jobId}`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      
      const newFavorites = new Set(favorites);
      if (favorites.has(jobId)) {
        newFavorites.delete(jobId);
      } else {
        newFavorites.add(jobId);
      }
      setFavorites(newFavorites);
      localStorage.setItem('favoriteJobs', JSON.stringify([...newFavorites]));
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  const goBack = () => {
    if (currentStep === 'jobs') {
      setCurrentStep('cities');
    } else if (currentStep === 'cities') {
      setCurrentStep('prefectures');
    } else {
      navigate(-1);
    }
  };

  const clearAreaSelection = () => {
    localStorage.removeItem('selectedPrefecture');
    localStorage.removeItem('selectedCity');
    setSelectedPrefecture('');
    setSelectedCity('');
    setCurrentStep('prefectures');
  };

  // Filter prefectures by search term
  const filteredPrefectures = ALL_PREFECTURES.filter(prefecture =>
    prefecture.name.includes(searchTerm)
  );

  // Filter cities by search term
  const filteredCities = cities.filter(city =>
    city.includes(searchTerm)
  );

  if (loading && currentStep === 'prefectures') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-md mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={goBack}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ChevronLeftIcon className="h-6 w-6 mr-1" />
            戻る
          </button>
          
          <div className="flex-1 text-center">
            <h1 className="text-lg font-semibold text-gray-900">
              {currentStep === 'prefectures' && 'エリアを選択'}
              {currentStep === 'cities' && '市区町村を選択'}
              {currentStep === 'jobs' && 'エリア内の求人'}
            </h1>
            {(selectedPrefecture || selectedCity) && (
              <p className="text-sm text-gray-500">
                {selectedPrefecture}{selectedCity && ` > ${selectedCity}`}
              </p>
            )}
          </div>

          {currentStep === 'jobs' && (
            <button
              onClick={() => setShowFilters(true)}
              className="flex items-center text-blue-600 hover:text-blue-700"
            >
              <FunnelIcon className="h-6 w-6" />
            </button>
          )}
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6">
        {/* Prefecture Selection */}
        {currentStep === 'prefectures' && (
          <div className="space-y-4">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2">都道府県を選択してください</h2>
              <p className="text-gray-600 mb-4">働きたいエリアの都道府県を選んでください</p>
              
              {/* User's current location suggestion */}
              {user && user.prefecture && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600 font-medium">現在のエリア</p>
                      <p className="text-blue-800">{user.prefecture} {user.city}</p>
                    </div>
                    <button
                      onClick={() => handlePrefectureSelect(user.prefecture)}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
                    >
                      選択
                    </button>
                  </div>
                </div>
              )}
              
              {/* Search box */}
              <div className="relative mb-4">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="都道府県を検索..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Region/List toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1 mb-4">
                <button
                  onClick={() => setShowRegions(true)}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    showRegions 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  地方別
                </button>
                <button
                  onClick={() => setShowRegions(false)}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    !showRegions 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  一覧
                </button>
              </div>
            </div>
            
            {/* Prefecture list */}
            {showRegions ? (
              // Region-based view
              <div className="space-y-4">
                {Object.keys(REGIONS).map((region) => {
                  const regionPrefectures = ALL_PREFECTURES.filter(p => p.region === region);
                  const filteredRegionPrefectures = regionPrefectures.filter(p => 
                    p.name.includes(searchTerm)
                  );
                  
                  if (filteredRegionPrefectures.length === 0) return null;
                  
                  return (
                    <div key={region} className="bg-white rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">{region}</h3>
                      <div className="grid grid-cols-2 gap-2">
                        {filteredRegionPrefectures.map((prefecture) => (
                          <button
                            key={prefecture.code}
                            onClick={() => handlePrefectureSelect(prefecture.name)}
                            className="text-left p-3 rounded-lg hover:bg-gray-50 transition-colors border border-gray-200"
                          >
                            <span className="font-medium text-gray-900">{prefecture.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              // Simple list view
              <div className="space-y-2">
                {filteredPrefectures.map((prefecture) => (
                  <button
                    key={prefecture.code}
                    onClick={() => handlePrefectureSelect(prefecture.name)}
                    className="w-full bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow text-left flex items-center justify-between"
                  >
                    <div>
                      <span className="font-medium text-gray-900">{prefecture.name}</span>
                      <span className="text-sm text-gray-500 ml-2">({prefecture.region})</span>
                    </div>
                    <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                  </button>
                ))}
              </div>
            )}
            
            {filteredPrefectures.length === 0 && (
              <div className="text-center py-12">
                <MapPinIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">検索結果が見つかりません</p>
              </div>
            )}
          </div>
        )}

        {/* City Selection */}
        {currentStep === 'cities' && (
          <div className="space-y-3">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2">市区町村を選択してください</h2>
              <p className="text-gray-600 mb-4">{selectedPrefecture}で求人がある市区町村</p>
              
              {/* Search box */}
              <div className="relative mb-4">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="市区町村を検索..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : filteredCities.length === 0 ? (
              <div className="text-center py-12">
                <MapPinIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">
                  {searchTerm ? '検索結果が見つかりません' : 'この都道府県には求人がありません'}
                </p>
                {!searchTerm && (
                  <button
                    onClick={clearAreaSelection}
                    className="mt-4 text-blue-600 hover:text-blue-700"
                  >
                    他の都道府県を選択
                  </button>
                )}
              </div>
            ) : (
              filteredCities.map((city) => (
                <button
                  key={city}
                  onClick={() => handleCitySelect(city)}
                  className="w-full bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow text-left flex items-center justify-between"
                >
                  <span className="font-medium text-gray-900">{city}</span>
                  <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                </button>
              ))
            )}
          </div>
        )}

        {/* Jobs List */}
        {currentStep === 'jobs' && (
          <div>
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  {selectedCity}の求人
                </h2>
                <button
                  onClick={clearAreaSelection}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  エリア変更
                </button>
              </div>
              <p className="text-gray-600 mb-4">
                {loading ? '検索中...' : `${jobs.length}件の求人が見つかりました`}
              </p>
              
              {/* Quick Filters */}
              <div className="flex gap-2 overflow-x-auto pb-2">
                <button
                  onClick={() => {
                    handleFilterChange('experienceRequired', filters.experienceRequired === false ? null : false);
                    applyFilters();
                  }}
                  className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${
                    filters.experienceRequired === false 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  未経験OK
                </button>
                <button
                  onClick={() => {
                    handleFilterChange('sameDayOk', filters.sameDayOk === true ? null : true);
                    applyFilters();
                  }}
                  className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${
                    filters.sameDayOk === true 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  即日勤務可
                </button>
                <button
                  onClick={() => {
                    handleFilterChange('sortBy', filters.sortBy === 'hourly_rate_desc' ? 'created_at' : 'hourly_rate_desc');
                    applyFilters();
                  }}
                  className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${
                    filters.sortBy === 'hourly_rate_desc' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  高時給順
                </button>
              </div>
            </div>
            
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : jobs.length === 0 ? (
              <div className="text-center py-12">
                <MapPinIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">このエリアに求人がありません</p>
                <button
                  onClick={clearAreaSelection}
                  className="mt-4 text-blue-600 hover:text-blue-700"
                >
                  他のエリアを検索
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {jobs.map((job) => (
                  <div key={job.id} className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                    {/* Job thumbnail */}
                    {job.thumbnail_image && (
                      <div className="w-full h-48 bg-gray-200">
                        <img
                          src={job.thumbnail_image}
                          alt={job.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    
                    <div className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                          {job.title}
                        </h3>
                        <button
                          onClick={() => toggleFavorite(job.id)}
                          className="ml-2 p-1"
                        >
                          {favorites.has(job.id) ? (
                            <HeartSolidIcon className="h-6 w-6 text-red-500" />
                          ) : (
                            <HeartIcon className="h-6 w-6 text-gray-400 hover:text-red-500" />
                          )}
                        </button>
                      </div>
                      
                      <div className="space-y-2 text-sm text-gray-600">
                        <div className="flex items-center">
                          <CurrencyYenIcon className="h-4 w-4 mr-1" />
                          <span className="font-medium text-blue-600">時給 {job.hourly_rate.toLocaleString()}円</span>
                        </div>
                        
                        <div className="flex items-center">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          <span>{job.work_hours}</span>
                        </div>
                        
                        <div className="flex items-center">
                          <MapPinIcon className="h-4 w-4 mr-1" />
                          <span>{job.city}</span>
                        </div>
                        
                        <div className="text-xs text-gray-500">
                          勤務日: {new Date(job.start_date).toLocaleDateString('ja-JP')}
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-1 mt-3">
                        {!job.experience_required && (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                            未経験OK
                          </span>
                        )}
                        {job.same_day_ok && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                            即日勤務可
                          </span>
                        )}
                        {job.tags && job.tags.map((tag, index) => (
                          <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                      
                      <button
                        onClick={() => navigate(`/jobs/${job.id}`)}
                        className="w-full mt-4 bg-blue-600 text-white py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                      >
                        詳細を見る
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Filter Modal */}
      {showFilters && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
          <div className="bg-white w-full max-h-[80vh] overflow-y-auto rounded-t-xl">
            <div className="sticky top-0 bg-white border-b px-4 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">フィルター</h3>
              <button
                onClick={() => setShowFilters(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                
              </button>
            </div>
            
            <div className="p-4 space-y-6">
              {/* Sort Options */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  並び順
                </label>
                <select
                  value={filters.sortBy}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="created_at">新着順</option>
                  <option value="hourly_rate_desc">時給が高い順</option>
                  <option value="hourly_rate_asc">時給が低い順</option>
                  <option value="start_date">勤務開始日順</option>
                </select>
              </div>

              {/* Hourly Rate Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  時給
                </label>
                <div className="flex gap-2 items-center">
                  <input
                    type="number"
                    placeholder="最低時給"
                    value={filters.minRate}
                    onChange={(e) => handleFilterChange('minRate', e.target.value)}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                  />
                  <span className="text-gray-500">〜</span>
                  <input
                    type="number"
                    placeholder="最高時給"
                    value={filters.maxRate}
                    onChange={(e) => handleFilterChange('maxRate', e.target.value)}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              </div>

              {/* Experience Required */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  経験
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleFilterChange('experienceRequired', null)}
                    className={`px-4 py-2 rounded-lg ${
                      filters.experienceRequired === null 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    すべて
                  </button>
                  <button
                    onClick={() => handleFilterChange('experienceRequired', false)}
                    className={`px-4 py-2 rounded-lg ${
                      filters.experienceRequired === false 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    未経験OK
                  </button>
                  <button
                    onClick={() => handleFilterChange('experienceRequired', true)}
                    className={`px-4 py-2 rounded-lg ${
                      filters.experienceRequired === true 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    経験必須
                  </button>
                </div>
              </div>

              {/* Same Day OK */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  勤務開始
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleFilterChange('sameDayOk', null)}
                    className={`px-4 py-2 rounded-lg ${
                      filters.sameDayOk === null 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    すべて
                  </button>
                  <button
                    onClick={() => handleFilterChange('sameDayOk', true)}
                    className={`px-4 py-2 rounded-lg ${
                      filters.sameDayOk === true 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    即日勤務可
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={resetFilters}
                  className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg font-medium"
                >
                  リセット
                </button>
                <button
                  onClick={applyFilters}
                  className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-medium"
                >
                  適用
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AreaSearchPage;